import { html } from "lit";
import { formatHtml } from "@trumpf/ux.common-storybook";
import { renderPageTemplate } from "../../../utils";
import { getDummyText, renderStandardPictureElement } from "../../../utils";

export default {
    title: "Website/Page Templates/TUS Form",
    parameters: {
        docs: {
            source: {
                transform: formatHtml,
            },
        },
    },
};

const defaultStoryArgs = {
    isSourceCode: false,
    formError: false,
    stepNumber: 1,
    openTechnicalDetails: false,
    isFirstPart: false,
};

const TusForm = (storyArgs = defaultStoryArgs) => {
    const getContainerClass = () => {
        const step = storyArgs.stepNumber;
        if (step === 1) return "technical-details";
        else if (step === 2) return "overview";
        else if (step === 3) return "add-part";
        else if (step === 4 || step === 5) return "edit-part";
        else if (step === 6) return "contact-details";
        else if (step === 7) return "confirmation";
    };
    const template = html`
        <!-- HERE GOES THE HEADER CONTENT OF THE PAGE, ALREADY AVAILABLE AS PARTIAL IN TYPO3 -->
        ${header()}
        <div class="ux-iws-tus-form" id="tus-form">
            <div class="ux-iws-tus-form__container">
                <div class="ux-iws-tus-form__steps-container">
                    <ux-steps>
                        <ux-steps-item
                            number="1"
                            done=${storyArgs.stepNumber > 5 ? "true" : "false"}
                            current=${storyArgs.stepNumber <= 5}
                        >
                            Your parts
                        </ux-steps-item>
                        <ux-steps-item
                            number="2"
                            done=${storyArgs.stepNumber === 7
                                ? "true"
                                : "false"}
                            current=${storyArgs.stepNumber === 6}
                        >
                            Your data
                        </ux-steps-item>
                    </ux-steps>
                </div>
                <div class="ux-iws-tus-form__${getContainerClass()}">
                    <ux-same-height
                        target-selectors=".ux-label"
                        item-container-selectors="div"
                    >
                        ${storyArgs.stepNumber === 1
                            ? html`${technicalDetailsForm(storyArgs)}`
                            : html``}
                        ${storyArgs.stepNumber === 2
                            ? html`${overview()}`
                            : html``}
                        ${storyArgs.stepNumber === 3
                            ? html`${additionalPart(storyArgs)}`
                            : html``}
                        ${storyArgs.stepNumber === 4
                            ? html`${editPart(storyArgs)}`
                            : html``}
                        ${storyArgs.stepNumber === 5
                            ? html`${partWithError(storyArgs)}`
                            : html``}
                        ${storyArgs.stepNumber === 6
                            ? html`${contactDetails(storyArgs)}`
                            : html``}
                        ${storyArgs.stepNumber === 7
                            ? html`${successTemplate()}`
                            : html``}
                    </ux-same-height>
                </div>
            </div>
        </div>
    `;
    return template;
};

export const TechnicalData = (args) => renderPageTemplate(() => TusForm(args));

TechnicalData.args = {
    ...defaultStoryArgs,
    isFirstPart: true,
};

function overview() {
    const template = html` ${thumbnails()}
        <div class="ux-iws-tus-form__controls">
            <a
                class="ux-button"
                href="./iframe.html?id=trumpf-com-tus-form--add-additional-part#tus-form"
            >
                Add another part
            </a>
            <a
                type="button"
                class="ux-button ux-button--green"
                href="./iframe.html?id=trumpf-com-tus-form--contact-details#tus-form"
            >
                go to the next step
            </a>
        </div>`;
    return template;
}

export const Overview = (args) => renderPageTemplate(() => TusForm(args));

Overview.args = {
    ...defaultStoryArgs,
    stepNumber: 2,
};

function additionalPart(storyArgs) {
    const template = html` ${thumbnails()} ${technicalDetailsForm(storyArgs)} `;
    return template;
}

export const AddAdditionalPart = (args) =>
    renderPageTemplate(() => TusForm(args));

AddAdditionalPart.args = {
    ...defaultStoryArgs,
    withCancelControls: true,
    stepNumber: 3,
};

function editPart(storyArgs) {
    const template = html`
        ${thumbnails(false)} ${technicalDetailsForm(storyArgs)}
    `;
    return template;
}

export const EditPart = (args) => renderPageTemplate(() => TusForm(args));

EditPart.args = {
    ...defaultStoryArgs,
    stepNumber: 4,
    openTechnicalDetails: true,
};

function partWithError(storyArgs) {
    const template = html`
        ${thumbnails(false)} ${technicalDetailsForm(storyArgs)}
    `;
    return template;
}

function thumbnails(multiple = true) {
    const template = html`
        <!-- HERE, THE PARTS THAT THE USER HAS ALREADY SUBMITTED SHALL BE LISTED -->
        <div class="ux-iws-tus-form__thumbnail-container ux-u-shadow">
            <div
                class="ux-text-headline ux-text-headline--medium ux-u-margin-bottom-30 ux-u-text-align--center"
            >
                Parts you want to return
            </div>
            ${thumbnail("68799UI", "1")}
            ${multiple ? html`${thumbnail("47933ZT", "2")}` : html``}
        </div>
    `;
    return template;
}

function thumbnail(partNumber, partIndex) {
    const thumbnail = html`
        <!-- THUMBNAIL -->
        <div class="ux-iws-tus-form__thumbnail">
            <div class="ux-iws-tus-form__thumbnail-content">
                <div class="ux-iws-tus-form__thumbnail-part-number ux-label">
                    Part ${partIndex}
                </div>
                <div
                    class="ux-iws-tus-form__thumbnail-description ux-text-normal ux-text-normal--light-gray"
                >
                    <div class="ux-iws-tus-form__thumbnail-element">
                        Reason for return: 03 = missing part
                    </div>
                    <div class="ux-iws-tus-form__thumbnail-element">
                        TRUMPF Part number: ${partNumber}
                    </div>
                </div>
            </div>
            <div class="ux-iws-tus-form__thumbnail-controls">
                <button
                    class="ux-icon-button ux-u-margin-right-15"
                    data-trigger-modal="open,delete-instance-modal-${partNumber}"
                >
                    <ux-icon name="delete" size-s="20"></ux-icon>
                </button>
                <a
                    class="ux-icon-button"
                    href="./iframe.html?id=trumpf-com-tus-form--edit-part#tus-form"
                >
                    <ux-icon name="edit" size-s="20"></ux-icon>
                </a>
            </div>
        </div>
        <!-- MODAL FOR DELETION OF THE PART -->
        <ux-modal name="delete-instance-modal-${partNumber}">
            <ux-modal-content class="ux-u-text-align--center">
                <div class="ux-text-headline ux-text-headline--big">
                    Delete part ${partNumber}?
                </div>
                <p class="ux-text-normal ux-text-normal--light-gray">
                    ${getDummyText(300)}
                </p>
                <div class="ux-button-group ux-button-group--with-spacing">
                    <button
                        data-trigger-modal="close,delete-instance-modal-${partNumber}"
                        class="ux-button"
                        type="button"
                    >
                        Cancel
                    </button>
                    <!-- ON CLICKING THIS LINK, THE PART SHALL BE DELETD, THE PAGE THAT THE LINK LEADS
                        TO SHOULD BE BASED ON THE STEP THAT THE USER IS CURRENTLY ON! EXAMPLE: IF THIS LINK
                        IS CLICKED WHILE ON THE OVERVIEW PAGE, THE LINK SHOULD LEAD BACK TO THE OVERVIEW PAGE.
                        THE SAME APPLIES FOR THE CONTACT DETAILS STEP.
                    -->
                    <a
                        class="ux-button ux-button--green"
                        href="./iframe.html?id=trumpf-com-tus-form--overview&part-to-delete=${partNumber}#tus-form"
                    >
                        Delete
                    </a>
                </div>
            </ux-modal-content>
        </ux-modal>
    `;
    return thumbnail;
}

function contactDetails(storyArgs) {
    return html`
        ${thumbnails()}
        <div class="row">
            <div class="gr-12 ux-u-text-align--center ux-u-margin-bottom-20">
                <div class="ux-text-headline ux-text-headline--medium">
                    How can we contact you?
                </div>
            </div>
            <div
                class="gr-10@large push-1@large gr-10@tMin768 push-1@tMin768 no-gutter gr-12@m push-0@m"
            >
                <ux-form>
                    <form method="${storyArgs.isSourceCode ? "post" : "get"}">
                        ${contactForm()} ${contactStepControls()}
                    </form>
                </ux-form>
            </div>
        </div>
    `;
}

export const ContactDetails = (args) => renderPageTemplate(() => TusForm(args));

ContactDetails.args = {
    ...defaultStoryArgs,
    stepNumber: 6,
};

function contactForm() {
    return html`
        <div class="row">
            <div class="gr-12">
                <div class="row">
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-select
                            full-width
                            label="form of address"
                            name="form-of-address"
                        >
                            <option value="" selected>Please select...</option>
                            <option value="1">Mr.</option>
                            <option value="1">Ms.</option>
                            <option value="1">Mx.</option>
                        </ux-select>
                    </div>
                    <div
                        class="gr-6 gr-12@m ux-u-margin-top-50 ux-u-margin-bottom-55 hide@m"
                    >
                        <!-- no form element assigned to this position -->
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="first name"
                            type="text"
                            name="first-name"
                            required
                            data-validation-required--msg="This field is required"
                        ></ux-input-field>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="last name"
                            type="text"
                            name="last-name"
                            required
                            data-validation-required--msg="This field is required"
                        ></ux-input-field>
                    </div>
                    <div class="gr-12 ux-u-margin-bottom-25">
                        <ux-input-field
                            label="company"
                            type="text"
                            name="company"
                            required
                            data-validation-required--msg="This field is required"
                        ></ux-input-field>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-phone-input
                            language="us"
                            label="phone"
                            country="US"
                            required
                            data-validation-required--msg="This field is required"
                            data-validation-invalid--msg="This field is invalid"
                        ></ux-phone-input>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="email"
                            type="email"
                            name="email"
                            required
                            data-validation-required--msg="This field is required"
                            data-validation-invalid--msg="This is not a valid email"
                        ></ux-input-field>
                    </div>
                    <div class="gr-12 ux-u-margin-bottom-25">
                        <div class="ux-text-tiny ux-u-text-uppercase">
                            Mandatory fields are marked with an *
                        </div>
                    </div>
                    <div class="gr-12 ux-u-margin-bottom-25">
                        <div class="ux-text-tiny ux-u-text-uppercase">
                            Further details can be read in our
                            <a
                                href="#"
                                class="ux-text-link ux-text-link--green"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                TRUMPF Datenschutz
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function contactStepControls() {
    return html`
        <div class="row">
            <div class="gr-12">
                <div class="ux-button-group">
                    <!-- BUTTON THAT GOES BACK TO THE OVERVIEW -->
                    <button
                        class="ux-button"
                        formnovalidate
                        formaction="./iframe.html#tus-form"
                        type="submit"
                        name="id"
                        value="trumpf-com-tus-form--overview"
                        value="back-button-on-form"
                    >
                        Back
                    </button>
                    <button
                        type="submit"
                        class="ux-button ux-button--green"
                        name="id"
                        value="trumpf-com-tus-form--success-page"
                        formaction="./iframe.html#tus-form"
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    `;
}

function successTemplate() {
    const template = html`
        <div class="row">
            <div class="gr-12 no-gutter ux-u-margin-bottom-50">
                <div class="row">
                    <div class="gr-10 push-1 ux-u-shadow">
                        <div
                            class="row ux-u-margin-vertical-30 ux-u-margin-l-vertical-50"
                        >
                            <div
                                class="gr-12 u-hide@t u-hide@d u-hide@large ux-u-text-align--center"
                            >
                                <ux-icon
                                    name="checkmark-rounded-thin"
                                    class="ux-color-success"
                                    size-s="45"
                                ></ux-icon>
                            </div>
                            <div
                                class="gr-12 ux-u-center-content ux-u-text-align--center"
                            >
                                <ux-icon
                                    name="checkmark-rounded-thin"
                                    class="ux-color-success u-hide@m ux-u-margin-right-15 ux-u-margin-bottom-10"
                                    size-s="50"
                                ></ux-icon>
                                <div
                                    class="ux-text-headline ux-text-headline--big"
                                >
                                    Thank you for your request
                                </div>
                            </div>
                            <div
                                class="gr-12 gr-8@large push-2@large ux-u-text-align--center"
                            >
                                <div
                                    class="ux-text-normal ux-text-normal--light-gray"
                                >
                                    ${getDummyText(190)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="gr-10 push-1">
                <div
                    class="ux-text-headline ux-text-headline--medium ux-u-margin-bottom-25 ux-u-text-align--center"
                >
                    The parts you want to return:
                </div>
                <!-- SINGLE OVERVIEW PART -->
                <div class="row">
                    <div
                        class="gr-12 gr-10@large push-1@large no-gutter ux-u-border-top"
                    >
                        <div class="row ux-u-margin-vertical-30">
                            <div class="gr-12 no-gutter">
                                <div
                                    class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase"
                                >
                                    Part 1
                                </div>
                            </div>
                            <div class="gr-12 gr-5@large no-gutter-left">
                                <div
                                    class="ux-text-normal ux-text-normal--light-gray ux-u-margin-top-10"
                                >
                                    Reason for return: 4 = Wrong part
                                </div>
                            </div>
                            <div class="gr-12 gr-6@large no-gutter-left">
                                <div
                                    class="ux-text-normal ux-text-normal--light-gray ux-u-margin-top-10"
                                >
                                    TRUMPF Part number: 123-456-789
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div
                        class="gr-12 gr-10@large push-1@large no-gutter ux-u-border-top"
                    >
                        <div class="row ux-u-margin-vertical-30">
                            <div class="gr-12 no-gutter">
                                <div
                                    class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase"
                                >
                                    Part 2
                                </div>
                            </div>
                            <div class="gr-12 gr-5@large no-gutter-left">
                                <div
                                    class="ux-text-normal ux-text-normal--light-gray ux-u-margin-top-10"
                                >
                                    Reason for return: 4 = Wrong part
                                </div>
                            </div>
                            <div class="gr-12 gr-6@large no-gutter-left">
                                <div
                                    class="ux-text-normal ux-text-normal--light-gray ux-u-margin-top-10"
                                >
                                    TRUMPF Part number: 123-456-789
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="gr-12 gr-10@large push-1@large ux-u-border-top">
                    <div class="row">
                        <div
                            class="gr-12 ux-u-text-align--center ux-u-margin-top-30 ux-u-margin-m-top-40"
                        >
                            <a href="#" class="ux-button"> Download pdf </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
    return template;
}

export const SuccessPage = (args) => renderPageTemplate(() => TusForm(args));

SuccessPage.args = {
    ...defaultStoryArgs,
    stepNumber: 7,
};

function header() {
    return html`
        <div class="row">
            <div
                class="block block-overview-head u-marginBottom-60 block-overview-head-short"
            >
                ${renderStandardPictureElement({ width: 1920, height: 600 })}
                <div
                    class="block-overview-head-content gr-10 push-1 gr-12@m push-0@m"
                >
                    <div class="gr-10 push-1 gr-12@m push-0@m">
                        <div class="gr-12 u-textCenter">
                            <h1 class="headline-big">Return part</h1>
                        </div>
                        <div class="text-normal">
                            <p>${getDummyText(370)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function technicalDetailsForm(storyArgs) {
    return html`
        <ux-form>
            <form
                action="./iframe.html#tus-form"
                method="${storyArgs.isSourceCode ? "post" : "get"}"
            >
                ${storyArgs.isSourceCode
                    ? html``
                    : html`<input
                          type="hidden"
                          name="id"
                          value="trumpf-com-tus-form--overview"
                      />`}
                <ux-static-filter>
                    <div class="row">
                        <div
                            class="gr-12 ux-u-text-align--center ux-u-margin-bottom-20"
                        >
                            <div
                                class="ux-text-headline ux-text-headline--medium"
                            >
                                Why do you want to return this part
                            </div>
                        </div>
                        <div
                            class="gr-10@large push-1@large gr-10@tMin768 push-1@tMin768 no-gutter@tMin768 gr-12 ux-u-margin-bottom-15 ux-u-margin-m-bottom-25"
                        >
                            <div class="gr-12 ux-u-margin-bottom-25">
                                <ux-select
                                    name="return"
                                    label="reason for return"
                                    full-width
                                    required
                                    data-validation-required--msg="This field is required"
                                >
                                    ${!storyArgs.openTechnicalDetails &&
                                    !storyArgs.formError
                                        ? html`<option value="" selected>
                                              Please select
                                          </option>`
                                        : html`<option value="">
                                              Please select
                                          </option>`}
                                    ${storyArgs.openTechnicalDetails ||
                                    storyArgs.formError
                                        ? html`<option value="1">
                                              01 = Wrong part
                                          </option>`
                                        : html`<option value="1" selected>
                                              01 = Wrong part
                                          </option>`}
                                    <option value="2">
                                        02 = Not working part
                                    </option>
                                    <option value="3">02 = Wrong order</option>
                                </ux-select>
                                <div
                                    class="ux-text-normal ux-text-normal--light-gray ux-u-margin-top-25"
                                    data-ux-static-filter="return="
                                >
                                    Mandatory fields are marked with *
                                </div>
                            </div>
                        </div>
                        <div
                            data-ux-static-filter="return=1,return=2,return=3"
                            class="row"
                        >
                            ${technicalDetails(storyArgs)}
                            ${technicalDetailsControls(storyArgs.isFirstPart)}
                        </div>
                    </div>
                    ${storyArgs.withCancelControls
                        ? html`<div
                              class="ux-iws-tus-form__controls"
                              data-ux-static-filter="return="
                          >
                              <a
                                  class="ux-button"
                                  href="./iframe.html?id=trumpf-com-tus-form--overview#tus-form"
                              >
                                  Cancel
                              </a>
                              <a
                                  class="ux-button ux-button--green"
                                  href="./iframe.html?id=trumpf-com-tus-form--contact-details#tus-form"
                              >
                                  Cancel and go to the next step
                              </a>
                          </div>`
                        : html``}
                </ux-static-filter>
            </form>
        </ux-form>
    `;
}

function technicalDetails(storyArgs) {
    return html`
        <div
            class="row u-paddingBottom-lg u-paddingTop-lg ux-u-background-grey"
        >
            <div
                class="gr-10@large push-1@large gr-10@tMin768 push-1@tMin768 no-gutter@tMin768 gr-12"
            >
                <div class="row">
                    <div class="gr-12 ux-u-text-align--center">
                        <div
                            class="ux-text-headline ux-text-headline--medium ux-u-margin-bottom-20"
                        >
                            Which part would you like to return?
                        </div>
                    </div>
                    <div
                        class="gr-12 ux-u-margin-bottom-25"
                        data-ux-static-filter="return=2"
                    >
                        <ux-date-picker
                            label="date"
                            top-label="date of failure"
                            name="date"
                            required
                            data-validation-invalid--msg="Please insert a valid date"
                            data-validation-required--msg="This field is required"
                        ></ux-date-picker>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="trumpf part number"
                            type="text"
                            value="${storyArgs.openTechnicalDetails ||
                            storyArgs.formError
                                ? "5372GB"
                                : ""}"
                            name="trumpf-part-number"
                            required
                            data-validation-required--msg="This field is required"
                        ></ux-input-field>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        ${!storyArgs.formError
                            ? html`<ux-input-field
                                  label="part serial number (put na of not applicable)"
                                  type="text"
                                  name="part-serial-number"
                                  required
                                  data-validation-required--msg="This field is required"
                              ></ux-input-field>`
                            : html`
                                  <!-- INPUT FIELD WITH FORM ERROR -->
                                  <ux-input-field
                                      label="part serial number (put na of not applicable)"
                                      type="text"
                                      name="part-serial-number"
                                      required
                                      data-validation-required--msg="This field is required"
                                      error
                                      error-msg="Custom error message"
                                  ></ux-input-field>
                              `}
                    </div>
                    <div class="gr-12 ux-u-margin-bottom-25">
                        <ux-textarea-field
                            label="part description"
                            type="text"
                            name="part-description"
                            required
                            data-validation-required--msg="This field is required"
                        ></ux-textarea-field>
                    </div>
                    <div class="gr-12 ux-u-text-align--center">
                        <div
                            class="ux-text-headline ux-text-headline--medium ux-u-margin-vertical-20"
                        >
                            From which order would you like to return the part?
                        </div>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="customer purchase order number"
                            type="text"
                            name="customer-purchase-order-number"
                            required
                            data-validation-required--msg="This field is required"
                        >
                            <ux-icon name="info"></ux-icon>
                        </ux-input-field>
                        <ux-tooltip tooltip-target="ux-icon"
                            >Text of the tooltip</ux-tooltip
                        >
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="trumpf service technicians on site (put na if not applicable)"
                            type="text"
                            name="service-technician"
                            required
                            data-validation-required--msg="This field is required"
                        >
                        </ux-input-field>
                    </div>
                    <div class="gr-12 ux-u-text-align--center">
                        <div
                            class="ux-text-headline ux-text-headline--medium ux-u-margin-vertical-20 ux-u-margin-top-30"
                        >
                            From which machine did you order this part?
                        </div>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="machine serial number"
                            type="text"
                            required
                            data-validation-required--msg="This field is required"
                        >
                        </ux-input-field>
                    </div>
                    <div class="gr-6 gr-12@m ux-u-margin-bottom-25">
                        <ux-input-field
                            label="operating hours"
                            type="text"
                            name="operating-hours"
                            required
                            data-validation-required--msg="This field is required"
                        ></ux-input-field>
                    </div>
                    <div class="gr-12 ux-u-margin-bottom-25">
                        <ux-input-field
                            label="error code (example: 5301 1004) (put na if not applicable)"
                            type="text"
                            name="error-code"
                        ></ux-input-field>
                    </div>
                    <div class="gr-12 ux-u-margin-bottom-25">
                        <ux-textarea-field
                            label="Error description"
                            name="error-description"
                            required
                            data-validation-required--msg="This field is required"
                        ></ux-textarea-field>
                    </div>
                    <div class="gr-12 ux-u-text-align--center">
                        <div
                            class="ux-text-headline ux-text-headline--medium ux-u-margin-vertical-20"
                        >
                            Feel free to add any other question that may help us
                        </div>
                    </div>
                    <div class="gr-12 ux-u-margin-bottom-25">
                        <ux-textarea-field
                            label="Additional message"
                            type="text"
                            name="additional-message"
                        ></ux-textarea-field>
                    </div>
                    <div class="gr-12">
                        <div class="ux-text-normal ux-text-normal--light-gray">
                            Mandatory fields are marked with an *
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function technicalDetailsControls(isFirstPart = false) {
    return html`
        <div class="row">
            <div
                class="gr-10@large push-1@large gr-10@tMin768 push-1@tMin768 no-gutter@tMin768 gr-12"
            >
                <div class="ux-button-group">
                    ${!isFirstPart
                        ? html`<a
                              class="ux-button"
                              href="./iframe.html?id=trumpf-com-tus-form--overview#tus-form"
                          >
                              Cancel
                          </a>`
                        : html``}
                    <button class="ux-button ux-button--green" type="submit">
                        save
                    </button>
                </div>
            </div>
        </div>
    `;
}

export const BackendErrorTechnicalDetail = (args) =>
    renderPageTemplate(() => TusForm(args));

BackendErrorTechnicalDetail.args = {
    ...defaultStoryArgs,
    stepNumber: 5,
    openTechnicalDetails: true,
    formError: true,
};
